/**
 * ResultsView - Retro-Futuristic Simulation Dashboard
 *
 * A distinctive CRT-inspired dashboard for displaying Flux PhD simulation results.
 * Features a prominent energy flow chart, Single Line Diagram, Sankey diagram, and phosphor glow effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, Chip, Collapse } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxTypography } from '@vortx/shared-ui';
import {
    Delete as DeleteIcon,
    History as HistoryIcon,
    Bolt as BoltIcon,
    WbSunny as SolarIcon,
    EvStation as EvIcon,
    PowerInput as GridIcon,
    Warning as LossIcon,
    BatteryChargingFull as BatteryIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area,
    XAxis, YAxis, Tooltip, ResponsiveContainer,
    Sankey,
} from 'recharts';
import {
    COLORS, fadeInUp, pulseGlow, flowAnimation, flowAnimationReverse, pulseNode,
    ScanlineOverlay, KeyFindingsCard, MethodologyTooltip, SingleLineDiagram,
    EfficiencyBadge, MetricPill, ChartTooltip, SankeyNode, SankeyLink,
    HistoryCard, WinnerBanner, ComparisonMetricCard, EfficiencyComparisonChart,
} from './results';
import { Skeleton } from './Skeleton';

// === Main Component ===
const ResultsView = ({
    result,
    selectedTopology,
    selectedTopologies = [],
    comparisonResults,
    storedSimulations = [],
    loadingHistory = false,
    onLoadSimulation,
    onDeleteSimulation,
    onNavigateToSimulation,
}) => {
    // Empty state (no result and no comparison)
    if (!result && !comparisonResults) {
        return (
            <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                position: 'relative',
                bgcolor: COLORS.bg.primary,
            }}>
                <ScanlineOverlay />

                <BoltIcon sx={{
                    fontSize: 64,
                    color: COLORS.accent,
                    animation: `${pulseGlow} 2s ease-in-out infinite`,
                    mb: 3,
                }} />

                <Typography sx={{
                    fontFamily: vortxTypography.heading,
                    fontSize: '1.8rem',
                    fontWeight: 700,
                    color: COLORS.accent,
                    textShadow: `0 0 30px ${alpha(COLORS.accent, 0.5)}`,
                    mb: 1,
                }}>
                    Simulation Results
                </Typography>

                {loadingHistory ? (
                    <Box sx={{ width: '100%', maxWidth: 420, mt: 3 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                            color: COLORS.text.secondary,
                            mb: 2,
                            textAlign: 'center',
                        }}>
                            Loading simulations...
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} sx={{
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 1,
                                    p: 2,
                                    animation: `${fadeInUp} 0.3s ease-out ${i * 0.1}s both`,
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Skeleton variant="text" width="50%" sx={{ bgcolor: 'rgba(255,255,255,0.08)', height: 16 }} />
                                        <Skeleton variant="text" width="25%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 14 }} />
                                    </Box>
                                    <Skeleton variant="text" width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 14 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : storedSimulations.length > 0 ? (
                    <Box sx={{ width: '100%', maxWidth: 420, mt: 3 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                            color: COLORS.text.secondary,
                            mb: 2,
                            textAlign: 'center',
                        }}>
                            Select a previous simulation
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {storedSimulations.slice(0, 5).map((sim, i) => (
                                <Box key={sim.id} sx={{ animation: `${fadeInUp} 0.3s ease-out ${i * 0.1}s both` }}>
                                    <HistoryCard
                                        simulation={sim}
                                        isActive={false}
                                        onLoad={onLoadSimulation}
                                        onDelete={onDeleteSimulation}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.9rem',
                        color: COLORS.text.muted,
                        mt: 2,
                    }}>
                        Run a simulation to see results
                    </Typography>
                )}
            </Box>
        );
    }

    // === COMPARISON VIEW ===
    if (comparisonResults) {
        // API returns: { results: [...], recommendation, efficiency_gain_pct }
        const { results: comparisonResultsArray, recommendation, efficiency_gain_pct } = comparisonResults;

        // Get the two results (results array is sorted by efficiency, best first)
        const result1 = comparisonResultsArray?.[0];
        const result2 = comparisonResultsArray?.[1];

        // Map results back to the selectedTopologies order
        const topo1 = selectedTopologies[0];
        const topo2 = selectedTopologies[1];
        const topology_1 = comparisonResultsArray?.find(r => r.topology_id === topo1?.id) || result1;
        const topology_2 = comparisonResultsArray?.find(r => r.topology_id === topo2?.id) || result2;

        // Winner info based on recommendation
        const winner = recommendation ? {
            topology_id: recommendation,
            efficiency_advantage_pct: efficiency_gain_pct || 0,
        } : null;

        // Helper to determine which topology has better value for a metric (lower is better for losses, higher is better for efficiency)
        const getWinnerIndex = (v1, v2, lowerIsBetter = false) => {
            if (v1 == null || v2 == null) return null;
            if (lowerIsBetter) return v1 < v2 ? 0 : v2 < v1 ? 1 : null;
            return v1 > v2 ? 0 : v2 > v1 ? 1 : null;
        };

        return (
            <Box sx={{
                height: '100%',
                overflow: 'auto',
                bgcolor: COLORS.bg.primary,
                position: 'relative',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255,255,255,0.02)' },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha(COLORS.accent, 0.3),
                    borderRadius: 3,
                },
            }}>
                <ScanlineOverlay />

                <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}>
                        <Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.heading,
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: COLORS.accent,
                                textShadow: `0 0 20px ${alpha(COLORS.accent, 0.5)}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                <BoltIcon sx={{ fontSize: 24 }} />
                                Topology Comparison
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                color: COLORS.text.secondary,
                            }}>
                                Comparing #{topo1?.id} vs #{topo2?.id}
                            </Typography>
                        </Box>
                        <Chip
                            label="COMPARISON COMPLETE"
                            sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                bgcolor: alpha(COLORS.efficiency, 0.15),
                                color: COLORS.efficiency,
                                border: `1px solid ${alpha(COLORS.efficiency, 0.4)}`,
                            }}
                        />
                    </Box>

                    {/* Winner Banner */}
                    {winner && (
                        <WinnerBanner winner={winner} topologies={selectedTopologies} />
                    )}

                    {/* Key Findings Narrative for Comparison */}
                    <KeyFindingsCard
                        isComparison={true}
                        comparisonData={comparisonResults}
                    />

                    {/* Efficiency Comparison Chart */}
                    <EfficiencyComparisonChart results={comparisonResults} topologies={selectedTopologies} />

                    {/* Detailed Metrics Comparison */}
                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: COLORS.text.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            mb: 2,
                        }}>
                            Detailed Comparison
                        </Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                        }}>
                            <ComparisonMetricCard
                                icon={SolarIcon}
                                label="Solar Generated"
                                value1={topology_1?.total_solar_kwh ? Math.round(topology_1.total_solar_kwh) : null}
                                value2={topology_2?.total_solar_kwh ? Math.round(topology_2.total_solar_kwh) : null}
                                unit="kWh"
                                color={COLORS.solar}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={getWinnerIndex(topology_1?.total_solar_kwh, topology_2?.total_solar_kwh)}
                            />
                            <ComparisonMetricCard
                                icon={EvIcon}
                                label="EV Consumption"
                                value1={topology_1?.total_ev_kwh ? Math.round(topology_1.total_ev_kwh) : null}
                                value2={topology_2?.total_ev_kwh ? Math.round(topology_2.total_ev_kwh) : null}
                                unit="kWh"
                                color={COLORS.ev}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={null} // Same for both
                            />
                            <ComparisonMetricCard
                                icon={GridIcon}
                                label="Grid Import"
                                value1={topology_1?.total_grid_import_kwh ? Math.round(topology_1.total_grid_import_kwh) : null}
                                value2={topology_2?.total_grid_import_kwh ? Math.round(topology_2.total_grid_import_kwh) : null}
                                unit="kWh"
                                color={COLORS.grid}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={getWinnerIndex(topology_1?.total_grid_import_kwh, topology_2?.total_grid_import_kwh, true)}
                            />
                            <ComparisonMetricCard
                                icon={LossIcon}
                                label="Total Losses"
                                value1={topology_1?.total_losses_kwh ? Math.round(topology_1.total_losses_kwh * 10) / 10 : null}
                                value2={topology_2?.total_losses_kwh ? Math.round(topology_2.total_losses_kwh * 10) / 10 : null}
                                unit="kWh"
                                color={COLORS.loss}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={getWinnerIndex(topology_1?.total_losses_kwh, topology_2?.total_losses_kwh, true)}
                            />
                            <ComparisonMetricCard
                                icon={BatteryIcon}
                                label="BESS Throughput"
                                value1={topology_1?.total_bess_throughput_kwh ? Math.round(topology_1.total_bess_throughput_kwh) : null}
                                value2={topology_2?.total_bess_throughput_kwh ? Math.round(topology_2.total_bess_throughput_kwh) : null}
                                unit="kWh"
                                color={COLORS.bess}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={null}
                            />
                            <ComparisonMetricCard
                                icon={GridIcon}
                                label="Grid Export"
                                value1={topology_1?.total_grid_export_kwh ? Math.round(topology_1.total_grid_export_kwh) : null}
                                value2={topology_2?.total_grid_export_kwh ? Math.round(topology_2.total_grid_export_kwh) : null}
                                unit="kWh"
                                color={COLORS.export}
                                topology1Name={topo1?.name || 'Topology 1'}
                                topology2Name={topo2?.name || 'Topology 2'}
                                winnerIndex={getWinnerIndex(topology_1?.total_grid_export_kwh, topology_2?.total_grid_export_kwh)}
                            />
                        </Box>
                    </Box>

                    {/* Summary insight */}
                    {winner && (
                        <Box sx={{
                            mt: 3,
                            p: 2,
                            bgcolor: alpha(COLORS.accent, 0.1),
                            borderRadius: 2,
                            border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                            animation: `${fadeInUp} 0.5s ease-out 0.4s both`,
                        }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: COLORS.text.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.15em',
                                mb: 1,
                            }}>
                                Summary
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.85rem',
                                color: COLORS.text.secondary,
                                lineHeight: 1.6,
                            }}>
                                {`Topology #${winner.topology_id} achieves ${winner.efficiency_advantage_pct?.toFixed(2) || '0.00'}% higher efficiency compared to the baseline, resulting in ${Math.abs(Math.round((topology_1?.total_losses_kwh || 0) - (topology_2?.total_losses_kwh || 0)))} kWh difference in total losses.`}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // === SINGLE RESULT VIEW ===
    // Use selectedTopology if provided, otherwise fall back to first in array
    const topology = selectedTopology || selectedTopologies[0];

    // Extract data
    const efficiencyPct = result.efficiency_pct ?? result.summary?.net_efficiency_pct;
    const totalLossKwh = result.total_losses_kwh ?? result.summary?.total_loss_kwh ?? 0;
    const totalSolarKwh = result.total_solar_kwh ?? result.summary?.total_solar_kwh ?? 0;
    const totalEvKwh = result.total_ev_kwh ?? 0;
    const totalGridImportKwh = result.total_grid_import_kwh ?? 0;
    const totalGridExportKwh = result.total_grid_export_kwh ?? 0;
    const totalBessChargeKwh = result.total_bess_charge_kwh ?? 0;
    const totalBessDischargeKwh = result.total_bess_discharge_kwh ?? 0;
    const totalBessThroughputKwh = result.total_bess_throughput_kwh ?? 0;

    // Check if BESS data is available in timeseries
    const hasBessData = result.timeseries?.bess_charge_kw && Array.isArray(result.timeseries.bess_charge_kw);

    // Timeseries data
    let timeseriesData = [];
    if (result.timeseries?.solar_kw && Array.isArray(result.timeseries.solar_kw)) {
        const length = Math.min(result.timeseries.solar_kw.length, 1440);
        const step = Math.max(1, Math.floor(length / 96));

        for (let i = 0; i < length; i += step) {
            const minuteOfDay = (result.timeseries.minute?.[i] ?? i) % 1440;
            const hour = Math.floor(minuteOfDay / 60);
            const minute = minuteOfDay % 60;

            timeseriesData.push({
                time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
                solar: result.timeseries.solar_kw?.[i] || 0,
                evLoad: result.timeseries.ev_load_kw?.[i] || 0,
                gridImport: result.timeseries.grid_import_kw?.[i] || 0,
                bessCharge: result.timeseries.bess_charge_kw?.[i] || 0,
                bessDischarge: result.timeseries.bess_discharge_kw?.[i] || 0,
                soc: result.timeseries.soc_pct?.[i] || 50,
            });
        }
    }

    // Build Sankey data
    const sankeyData = useMemo(() => {
        const nodes = [
            { name: 'Solar PV' },
            { name: 'Grid' },
            { name: 'EV Charging' },
            { name: 'Losses' },
        ];

        if (totalBessThroughputKwh > 0) {
            nodes.splice(2, 0, { name: 'BESS' });
        }

        if (totalGridExportKwh > 0) {
            nodes.push({ name: 'Export' });
        }

        const links = [];
        const nodeIndex = (name) => nodes.findIndex(n => n.name === name);

        // Solar to EV (direct path)
        const solarToEv = Math.min(totalSolarKwh, totalEvKwh) * (efficiencyPct / 100 || 0.9);
        const solarLosses = totalSolarKwh * (1 - (efficiencyPct / 100 || 0.9)) * 0.5;

        if (solarToEv > 1) {
            links.push({ source: nodeIndex('Solar PV'), target: nodeIndex('EV Charging'), value: Math.round(solarToEv) });
        }
        if (solarLosses > 1) {
            links.push({ source: nodeIndex('Solar PV'), target: nodeIndex('Losses'), value: Math.round(solarLosses) });
        }

        // Solar to BESS (charging)
        if (totalBessChargeKwh > 1 && nodeIndex('BESS') >= 0) {
            links.push({ source: nodeIndex('Solar PV'), target: nodeIndex('BESS'), value: Math.round(totalBessChargeKwh) });
        }

        // BESS to EV (discharging)
        if (totalBessDischargeKwh > 1 && nodeIndex('BESS') >= 0) {
            links.push({ source: nodeIndex('BESS'), target: nodeIndex('EV Charging'), value: Math.round(totalBessDischargeKwh * 0.95) });
            links.push({ source: nodeIndex('BESS'), target: nodeIndex('Losses'), value: Math.round(totalBessDischargeKwh * 0.05) });
        }

        if (totalGridImportKwh > 1) {
            const gridToEv = totalGridImportKwh * 0.95;
            const gridLosses = totalGridImportKwh * 0.05;
            links.push({ source: nodeIndex('Grid'), target: nodeIndex('EV Charging'), value: Math.round(gridToEv) });
            if (gridLosses > 1) {
                links.push({ source: nodeIndex('Grid'), target: nodeIndex('Losses'), value: Math.round(gridLosses) });
            }
        }

        if (totalGridExportKwh > 1) {
            links.push({ source: nodeIndex('Solar PV'), target: nodeIndex('Export'), value: Math.round(totalGridExportKwh) });
        }

        const validLinks = links.filter(l => l.value > 0 && l.source >= 0 && l.target >= 0 && l.source !== l.target);

        if (validLinks.length === 0) {
            return null;
        }

        return { nodes, links: validLinks };
    }, [totalSolarKwh, totalEvKwh, totalGridImportKwh, totalGridExportKwh, totalBessChargeKwh, totalBessDischargeKwh, totalBessThroughputKwh, totalLossKwh, efficiencyPct]);

    return (
        <Box sx={{
            height: '100%',
            overflow: 'auto',
            bgcolor: COLORS.bg.primary,
            position: 'relative',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255,255,255,0.02)' },
            '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(COLORS.accent, 0.3),
                borderRadius: 3,
            },
        }}>
            <ScanlineOverlay />

            <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                {/* Header Row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.heading,
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: COLORS.accent,
                                textShadow: `0 0 20px ${alpha(COLORS.accent, 0.5)}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                <BoltIcon sx={{ fontSize: 24 }} />
                                Simulation Analysis
                            </Typography>
                            {topology && (
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.8rem',
                                    color: COLORS.text.secondary,
                                }}>
                                    Topology #{topology.id}: {topology.name}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EfficiencyBadge value={efficiencyPct} />
                        <Chip
                            label={result.status === 'success' || result.status === 'completed' ? 'COMPLETE' : result.status?.toUpperCase() || 'READY'}
                            sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                bgcolor: alpha(COLORS.efficiency, 0.15),
                                color: COLORS.efficiency,
                                border: `1px solid ${alpha(COLORS.efficiency, 0.4)}`,
                            }}
                        />
                    </Box>
                </Box>

                {/* Key Findings Narrative */}
                <KeyFindingsCard result={result} topology={topology} />

                {/* HERO: Energy Flow Chart */}
                <Box sx={{
                    p: 3,
                    bgcolor: COLORS.bg.card,
                    borderRadius: 2,
                    border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                    mb: 3,
                    animation: `${fadeInUp} 0.5s ease-out`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: `linear-gradient(90deg, ${COLORS.solar}, ${COLORS.ev}, ${COLORS.grid})`,
                        boxShadow: `0 0 20px ${alpha(COLORS.accent, 0.5)}`,
                    },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: COLORS.text.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                        }}>
                            Energy Flow — 24 Hour Profile
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Solar', color: COLORS.solar },
                                { label: 'EV Load', color: COLORS.ev },
                                ...(hasBessData ? [{ label: 'BESS Charge', color: COLORS.bess }] : []),
                                { label: 'Grid Import', color: COLORS.grid },
                            ].map(item => (
                                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 1,
                                        bgcolor: item.color,
                                        boxShadow: `0 0 8px ${alpha(item.color, 0.6)}`,
                                    }} />
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.7rem',
                                        color: COLORS.text.secondary,
                                    }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {timeseriesData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={320}>
                            <AreaChart data={timeseriesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gradSolar" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={COLORS.solar} stopOpacity={0.7} />
                                        <stop offset="100%" stopColor={COLORS.solar} stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="gradEv" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={COLORS.ev} stopOpacity={0.7} />
                                        <stop offset="100%" stopColor={COLORS.ev} stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="gradGrid" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={COLORS.grid} stopOpacity={0.6} />
                                        <stop offset="100%" stopColor={COLORS.grid} stopOpacity={0.05} />
                                    </linearGradient>
                                    <filter id="glow">
                                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                                        <feMerge>
                                            <feMergeNode in="coloredBlur" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>
                                <XAxis
                                    dataKey="time"
                                    tick={{ fill: COLORS.text.muted, fontSize: 11, fontFamily: vortxTypography.mono }}
                                    interval={11}
                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fill: COLORS.text.muted, fontSize: 11, fontFamily: vortxTypography.mono }}
                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                    tickLine={false}
                                    width={50}
                                    label={{
                                        value: 'kW',
                                        angle: -90,
                                        position: 'insideLeft',
                                        fill: COLORS.text.muted,
                                        fontSize: 11,
                                        fontFamily: vortxTypography.mono,
                                    }}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="solar"
                                    stroke={COLORS.solar}
                                    fill="url(#gradSolar)"
                                    strokeWidth={2.5}
                                    name="Solar"
                                    filter="url(#glow)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="evLoad"
                                    stroke={COLORS.ev}
                                    fill="url(#gradEv)"
                                    strokeWidth={2.5}
                                    name="EV Load"
                                    filter="url(#glow)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="gridImport"
                                    stroke={COLORS.grid}
                                    fill="url(#gradGrid)"
                                    strokeWidth={2.5}
                                    name="Grid Import"
                                    filter="url(#glow)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: COLORS.text.muted, fontFamily: vortxTypography.mono }}>
                                No timeseries data available
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Metrics Row with Methodology Tooltips */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}>
                    <MethodologyTooltip metricKey="solar">
                        <MetricPill
                            icon={SolarIcon}
                            value={totalSolarKwh ? Math.round(totalSolarKwh).toLocaleString() : '--'}
                            label="Solar Generated"
                            unit="kWh"
                            color={COLORS.solar}
                            delay={0.1}
                        />
                    </MethodologyTooltip>
                    <MethodologyTooltip metricKey="ev">
                        <MetricPill
                            icon={EvIcon}
                            value={totalEvKwh ? Math.round(totalEvKwh).toLocaleString() : '--'}
                            label="EV Consumption"
                            unit="kWh"
                            color={COLORS.ev}
                            delay={0.2}
                        />
                    </MethodologyTooltip>
                    <MethodologyTooltip metricKey="bess">
                        <MetricPill
                            icon={BatteryIcon}
                            value={totalBessThroughputKwh ? Math.round(totalBessThroughputKwh).toLocaleString() : '--'}
                            label="BESS Throughput"
                            unit="kWh"
                            color={COLORS.bess}
                            delay={0.25}
                        />
                    </MethodologyTooltip>
                    <MethodologyTooltip metricKey="grid">
                        <MetricPill
                            icon={GridIcon}
                            value={totalGridImportKwh ? Math.round(totalGridImportKwh).toLocaleString() : '--'}
                            label="Grid Import"
                            unit="kWh"
                            color={COLORS.grid}
                            delay={0.3}
                        />
                    </MethodologyTooltip>
                    <MethodologyTooltip metricKey="losses">
                        <MetricPill
                            icon={LossIcon}
                            value={totalLossKwh ? totalLossKwh.toFixed(1) : '--'}
                            label="Total Losses"
                            unit="kWh"
                            color={COLORS.loss}
                            delay={0.4}
                        />
                    </MethodologyTooltip>
                </Box>

                {/* Single Line Diagram */}
                <Box sx={{
                    p: 3,
                    bgcolor: COLORS.bg.card,
                    borderRadius: 2,
                    border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                    mb: 3,
                    animation: `${fadeInUp} 0.5s ease-out 0.3s both`,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.text.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        mb: 2,
                    }}>
                        Single Line Diagram
                    </Typography>
                    <SingleLineDiagram topology={topology} result={result} />
                </Box>

                {/* Sankey Diagram */}
                {sankeyData && (
                    <Box sx={{
                        p: 3,
                        bgcolor: COLORS.bg.card,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.05)',
                        mb: 3,
                        animation: `${fadeInUp} 0.5s ease-out 0.5s both`,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: COLORS.text.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            mb: 2,
                        }}>
                            Energy Flow Diagram
                        </Typography>
                        <ResponsiveContainer width="100%" height={200}>
                            <Sankey
                                data={sankeyData}
                                node={<SankeyNode />}
                                link={<SankeyLink />}
                                nodePadding={40}
                                nodeWidth={16}
                                margin={{ top: 20, right: 120, bottom: 20, left: 120 }}
                            />
                        </ResponsiveContainer>
                    </Box>
                )}

                {/* Grid Export Banner */}
                {totalGridExportKwh > 0 && (
                    <Box sx={{
                        p: 2,
                        bgcolor: alpha(COLORS.export, 0.1),
                        borderRadius: 2,
                        border: `1px solid ${alpha(COLORS.export, 0.3)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        animation: `${fadeInUp} 0.5s ease-out 0.6s both`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                p: 1,
                                borderRadius: '50%',
                                bgcolor: alpha(COLORS.export, 0.2),
                            }}>
                                <GridIcon sx={{ color: COLORS.export, fontSize: 20 }} />
                            </Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                color: COLORS.text.secondary,
                            }}>
                                Exported to Grid
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: COLORS.export,
                            textShadow: `0 0 15px ${alpha(COLORS.export, 0.5)}`,
                        }}>
                            {Math.round(totalGridExportKwh).toLocaleString()} kWh
                        </Typography>
                    </Box>
                )}

                {/* Simulation History */}
                {storedSimulations.length > 1 && (
                    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out 0.7s both` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <HistoryIcon sx={{ color: COLORS.text.muted, fontSize: 16 }} />
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: COLORS.text.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.15em',
                            }}>
                                Previous Simulations
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                            gap: 1,
                        }}>
                            {storedSimulations.slice(0, 6).map((sim) => (
                                <HistoryCard
                                    key={sim.id}
                                    simulation={sim}
                                    isActive={sim.id === result?.simulation_id}
                                    onLoad={onLoadSimulation}
                                    onDelete={onDeleteSimulation}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ResultsView;
